import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiService } from '@/services/api'
import { useOptionalTeamContext } from '@/contexts/TeamContext'
import type { PlanTier } from 'aeo-schema-generator-shared/types'
import { FREE_TIER_PAGE_SETUP_LIMIT } from 'aeo-schema-generator-shared/utils'

export interface TierUsageData {
  tier: PlanTier
  effectiveTier?: Exclude<PlanTier, 'legacy_pro'>
  displayName?: string
  accountMode?: 'subscription' | 'legacy_credit_pack' | 'free_trial' | 'admin_grant' | 'expired_subscription_readonly'
  planSourceLabel?: string
  planDescription?: string
  grant?: {
    id: string
    teamId?: string | null
    planTier: PlanTier | 'legacy_pro'
    expiresAt: string
    status: string
  } | null
  isGrandfathered: boolean
  isGrandfatheredPrice?: boolean
  limits: {
    urls: number
    monitoredPages: number
    domains: number
    teamMembers: number
  }
  usage: {
    urls: number
    monitoredPages: number
    domains: number
    teamMembers: number
  }
  features: Record<string, any>
  entitlements?: {
    canGenerateSchema: boolean
    schemaCostMode: 'unlimited' | 'free_trial_limit' | 'credit' | 'expired_subscription_readonly'
    canGenerateHostedAlternateFiles: boolean
    canActivateMonitoring: boolean
    monitoringCap: number
    remainingCredits: number
    upgradeReason:
      | 'trial_page_limit'
      | 'free_trial_generated_page_limit'
      | 'credits_required'
      | 'subscription_required_for_alternate_files'
      | 'subscription_required_for_monitoring'
      | 'subscription_ended_read_only'
      | null
    generatedPagesUsed: number
    generatedPageLimit: number
    monitoredUrlsUsed: number
    isPaidSubscriber: boolean
    hasPurchasedCredits: boolean
    isExpiredSubscriberReadOnly?: boolean
    planTier: PlanTier
  }
  billingInterval: 'month' | 'year' | null
  periodEnd: string | null
  /** One of the team's free first pages went live. Absent from an older server's answer. */
  freeFirstPageUsed?: boolean
}

export interface TierInfo {
  tier: string
  effectiveTier?: string
  name: string
  displayName?: string
  description: string
  urlLimit: number
  monitoredPageLimit?: number
  domainLimit: number
  teamMemberLimit: number
  monthlyCitationScanLimit?: number
  features: string[]
  planFeatures: Record<string, any>
  isSelfServe?: boolean
  isContactSales?: boolean
  pricing: Record<string, { planId: string; priceInCents: number }>
}

/**
 * Hook to get the current team's tier status and usage.
 * Used by dashboard plan bar, billing page, upgrade modals, and generation flow.
 */
export function useTierStatus() {
  const teamContext = useOptionalTeamContext()
  const teamId = teamContext?.currentTeam?.team?.id
  const isWaitingForTeamContext =
    Boolean(teamContext) &&
    !teamId &&
    (teamContext?.isLoading === true || teamContext?.isLoadingCurrentTeam === true)
  const shouldFetchTierUsage = Boolean(teamId)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['tier-usage', teamId],
    queryFn: async () => {
      const response = await apiService.getSubscriptionUsage()
      return response.data as TierUsageData
    },
    enabled: shouldFetchTierUsage,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  })

  const tier = data?.tier ?? 'free'
  const effectiveTier = data?.effectiveTier ?? (tier === 'legacy_pro' ? 'growth' : tier)
  const displayName = data?.displayName ?? (tier === 'legacy_pro' ? 'Growth' : undefined)
  const accountMode = data?.accountMode ?? (tier === 'free' ? 'free_trial' : 'subscription')
  const planSourceLabel = data?.planSourceLabel
  const planDescription = data?.planDescription
  const isGrandfathered = data?.isGrandfathered ?? false
  const isGrandfatheredPrice = data?.isGrandfatheredPrice ?? false
  // Fallback for a response that predates the entitlements payload. It used to
  // assume generation was allowed and that the free allowance was three pages,
  // which is now wrong on both counts: the server would refuse the generate,
  // and the copy driven off this would promise pages nobody gets. Derive it
  // from the tier and the shared limit instead of hardcoding optimism.
  const isPaidTier = tier !== 'free'
  const entitlements = data?.entitlements ?? {
    canGenerateSchema: isPaidTier,
    schemaCostMode: isPaidTier ? ('unlimited' as const) : ('free_trial_limit' as const),
    canGenerateHostedAlternateFiles: isPaidTier,
    canActivateMonitoring: false,
    monitoringCap: 0,
    remainingCredits: 0,
    upgradeReason: isPaidTier ? null : ('free_trial_generated_page_limit' as const),
    generatedPagesUsed: data?.usage.urls ?? 0,
    generatedPageLimit: isPaidTier ? -1 : FREE_TIER_PAGE_SETUP_LIMIT,
    monitoredUrlsUsed: data?.usage.monitoredPages ?? 0,
    isPaidSubscriber: isPaidTier,
    hasPurchasedCredits: false,
    isExpiredSubscriberReadOnly: false,
    planTier: tier,
  }
  const isNewTierUser = entitlements.schemaCostMode !== 'credit'
  const shouldShowCredits = entitlements.schemaCostMode === 'credit'
  const isPro = entitlements.isPaidSubscriber

  return {
    tier,
    effectiveTier,
    displayName,
    accountMode,
    planSourceLabel,
    planDescription,
    grant: data?.grant ?? null,
    isGrandfathered,
    isGrandfatheredPrice,
    isPro,
    isNewTierUser,
    shouldShowCredits,
    entitlements,
    /**
     * Whether `entitlements` reflects a real response rather than the derived
     * fallback. Callers that gate a feature on it must not act on a guess: the
     * fallback assumes 'free' whenever the query has not run, which would show
     * a paywall to a paying customer on any surface that renders before team
     * context resolves.
     */
    isEntitlementsKnown: Boolean(data),
    limits: data?.limits ?? { urls: 3, monitoredPages: 0, domains: 1, teamMembers: 1 },
    usage: data?.usage ?? { urls: 0, monitoredPages: 0, domains: 0, teamMembers: 0 },
    features: data?.features ?? {},
    billingInterval: data?.billingInterval ?? null,
    periodEnd: data?.periodEnd ?? null,
    /** Only a plain yes from the server counts: the paywall must not say a page was free on a guess. */
    freeFirstPageUsed: data?.freeFirstPageUsed === true,
    isLoading: isLoading || isWaitingForTeamContext,
    error,
    refetch,
  }
}

/**
 * Hook to get all available subscription tiers for the pricing page.
 */
export function useSubscriptionTiers() {
  return useQuery({
    queryKey: ['subscription-tiers'],
    queryFn: async () => {
      const response = await apiService.getSubscriptionTiers()
      return (response.data || []) as TierInfo[]
    },
    staleTime: 24 * 60 * 60 * 1000,
  })
}

/**
 * Hook to change subscription tier (upgrade or downgrade).
 */
export function useChangeTier() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (planId: string) => {
      const response = await apiService.changeSubscriptionTier(planId)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tier-usage'] })
      queryClient.invalidateQueries({ queryKey: ['current-subscription'] })
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] })
      queryClient.invalidateQueries({ queryKey: ['user-profile'] })
    },
  })
}
